import { useState } from 'preact/hooks'
import { Download, Eye, Loader2, MessageSquareText } from 'lucide-preact'
import type { WebViewDto } from '@/api/webs'
import { webinarRoomApi } from '@/api/webinarRoom'
import { useAuth } from '@/auth/useAuth'
import { holdsCompanyAdminRole } from '@/components/console/admin/useCompanyAdminAccess'
import { useBrandGuidelines } from '@/components/knowledgeBase/brandGuidelines/useBrandGuidelines'
import { useCompany } from '@/hooks/useCompany'
import { useRoomRecordingLive } from '@/hooks/useRoomRecordingLive'
import { useToast } from '@/hooks/useToast'
import type { WebTabKey } from '@/hooks/useWebTabRouter'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { triggerBlobDownload } from '@/utils/downloadFile'
import { openTabOnClick, sendTabTo } from '@/utils/newTab'
import { routes } from '@/utils/routes'
import { roomViewerLink, sessionHasEnded } from '@/utils/webinarRoomSetup'
import { TRANSCRIPT_FALLBACK_FILE_NAME } from '@/webinarRoom/limits'
import { SignedOffNote } from '../panels/SignedOffNote'
import { RoomPollsCard } from '../panels/room/RoomPollsCard'
import { RoomRecordingCard } from '../panels/room/RoomRecordingCard'
import { RoomWaitingVideoCard } from '../panels/room/RoomWaitingVideoCard'
import { logoForLightBackgrounds } from '../panels/room/roomWaiting'
import { brandGuidelinesLink, questionsEmailedText, rehearsalLead } from '../panels/room/roomIntro'
import { BUTTON, LINK_BUTTON } from '../panels/room/roomStyles'

interface WebRoomTabProps {
    web: WebViewDto
    clientCompanyId: string
    campaignId: string
    onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void
    /** WebDetail's reread of the web, so the header's sign-off list moves with the recording. */
    reload: () => Promise<void>
    routeToTab: (tab: WebTabKey) => void
}

/**
 * An OrbitalX-room web's Room tab (ORB-1086): the rehearsal and the brand before the session, the transcript after
 * it, then the recording, the waiting screen (ORB-1118) and the polls. WebDetail renders it only for room webs.
 */
export function WebRoomTab({ web, clientCompanyId, campaignId, onPatchWeb, reload, routeToTab }: WebRoomTabProps) {
    const event = web.OnlineEvent!
    const workspaceId = web.WorkspaceId
    const isSignedOff = web.ApprovalStatus === 'Signed Off'
    const { canEdit } = useWorkspaceCapabilities()
    const { userCompanyRoleSets } = useAuth()
    const { workspaces } = useCompany()
    const { showError, showErrorUnless422, showSuccess } = useToast()
    const brand = useBrandGuidelines({ clientCompanyId, workspaceId, campaignId })
    const [opening, setOpening] = useState(false)
    const [downloading, setDownloading] = useState(false)

    useRoomRecordingLive(workspaceId, web.Id, () => void reload())

    const viewerLink = roomViewerLink(event)
    const ended = !!viewerLink && sessionHasEnded(event, new Date())
    const brandEntry = brand.load.kind === 'ready' ? brand.load.entry : null
    const accent = brandEntry?.BrandTokens?.Roles.Primary.Hex ?? null
    const brandLink = brandGuidelinesLink(
        { clientCompanyId, workspaceId, campaignId },
        { isCompanyAdmin: holdsCompanyAdminRole(userCompanyRoleSets, clientCompanyId), canEdit },
        brandEntry?.EffectiveLevel,
    )
    const clientName = workspaces.find(workspace => workspace.Id === workspaceId)?.ClientCompanyName?.trim()
    const roomBrand = {
        primary: accent,
        logoUrlOnLight: logoForLightBackgrounds(brandEntry?.Files ?? []),
        companyName: clientName ?? '',
    }
    const roomUrl = `${routes.scoped(clientCompanyId, workspaceId).web(campaignId, web.Id)}/live`

    const previewAsViewer = async () => {
        if (opening) return
        const previewWindow = openTabOnClick()
        if (!previewWindow) {
            showError('Preview not opened', 'Your browser blocked the new tab. Allow pop-ups for this site and try again.')
            return
        }
        setOpening(true)
        const res = await webinarRoomApi.getPreviewLink(workspaceId, web.Id)
        setOpening(false)
        if (!res.data) {
            previewWindow.close()
            showErrorUnless422('Preview not opened', res, 'The room page could not be opened.')
            return
        }
        if (!sendTabTo(previewWindow, res.data.Url)) showError('Preview not opened', 'The room page link was not a secure address.')
    }

    const downloadTranscript = async () => {
        setDownloading(true)
        const res = await webinarRoomApi.downloadTranscript(workspaceId, web.Id)
        setDownloading(false)
        if (!res.blob) {
            showError('Download failed', res.error || 'The transcript could not be downloaded.')
            return
        }
        const fileName = res.filename || TRANSCRIPT_FALLBACK_FILE_NAME
        triggerBlobDownload(res.blob, fileName)
        showSuccess('Download started', `${fileName}, every message and question with names and times.`)
    }

    return (
        <div>
            {isSignedOff && <SignedOffNote locked="the recording, waiting video or image, and polls" stillAllowed="You can still preview them." />}
            <div className="mb-6 flex flex-wrap items-start justify-between gap-x-8 gap-y-3">
                <div className="flex-[1_1_420px] min-w-0 max-w-[80ch] flex flex-col gap-1 text-sm text-slate-600 dark:text-slate-300">
                    {ended ? (
                        <>
                            <p><b className="font-semibold text-slate-900 dark:text-white">Chat and Q&amp;A transcript.</b> Every message and question, with names and times.</p>
                            <p>{questionsEmailedText(event)}</p>
                        </>
                    ) : (
                        <>
                            <p>
                                {rehearsalLead(event)}{' '}
                                <span className="whitespace-nowrap"><button type="button" className={LINK_BUTTON} onClick={() => routeToTab('details')}>Details tab</button>.</span>
                                {viewerLink ? '' : ' The viewer link is set when you sign off.'}
                            </p>
                            <p>
                                The room uses the logos for light and dark backgrounds from {clientName ? `${clientName}’s Brand Guidelines` : 'the Brand Guidelines'}.{' '}
                                <a
                                    href={brandLink.href}
                                    className={LINK_BUTTON}
                                    title={brandLink.canChange ? 'Opens the Brand Guidelines this room uses' : 'Opens this campaign’s Brand Guidelines'}
                                >
                                    {brandLink.canChange ? 'Edit brand' : 'See brand'}
                                </a>
                            </p>
                        </>
                    )}
                </div>
                {canEdit && (
                    <div className="flex flex-wrap gap-2">
                        {ended ? (
                            <>
                                <a href={roomUrl} target="_blank" rel="noopener noreferrer" className={BUTTON} title="Opens the ended room, with the full chat and Q&A, in a new tab">
                                    <MessageSquareText className="w-3.5 h-3.5" />Open transcript
                                </a>
                                <button type="button" className={BUTTON} disabled={downloading} onClick={() => void downloadTranscript()}>
                                    {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}Download
                                </button>
                            </>
                        ) : (
                            <button type="button" className={BUTTON} disabled={opening} title="Opens the room page as a viewer sees it, in a new tab" onClick={() => void previewAsViewer()}>
                                {opening ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}Preview as a viewer
                            </button>
                        )}
                    </div>
                )}
            </div>
            {/* Zero-minimum tracks: an untruncated file name's width would otherwise widen a card over its neighbour. */}
            <div className="grid gap-6 items-start grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
                <div className="grid gap-6 grid-cols-[minmax(0,1fr)]">
                    <RoomRecordingCard web={web} isSignedOff={isSignedOff} ended={ended} reload={reload} onPatchWeb={onPatchWeb} />
                    <RoomWaitingVideoCard web={web} isSignedOff={isSignedOff} brand={roomBrand} reload={reload} onPatchWeb={onPatchWeb} />
                </div>
                <RoomPollsCard web={web} isSignedOff={isSignedOff} onPatchWeb={onPatchWeb} accent={accent} />
            </div>
        </div>
    )
}
